import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useSetupTour } from './SetupTourContext';
import { cn } from '@/lib/utils';
import { getSpotlightGeometry, shouldScrollTargetIntoView } from './spotlightGeometry';

const MAX_RETRIES = 15;
const RETRY_INTERVAL = 300;
const PRESENCE_CHECK_INTERVAL = 400;

export function SetupTourSpotlight() {
  const { t } = useTranslation();
  const { status, currentStep, currentStepIndex, totalSteps, nextStep, prevStep, skipSetupTour } = useSetupTour();
  const [spotlight, setSpotlight] = useState<ReturnType<typeof getSpotlightGeometry> | null>(null);
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const rafRef = useRef<number>();

  // Reset visibility when step changes
  useEffect(() => {
    setVisible(false);
    setSpotlight(null);
    retryCountRef.current = 0;
  }, [currentStepIndex, currentStep?.id]);

  const positionTooltip = useCallback((rect: DOMRect) => {
    if (!currentStep) return;

    const padding = 12;
    const tooltipWidth = Math.min(320, window.innerWidth - 32);
    const placement = currentStep.placement || 'bottom';

    let top = 0;
    let left = 0;

    if (placement === 'bottom') {
      top = rect.bottom + padding;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (placement === 'top') {
      top = rect.top - padding;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (placement === 'right') {
      top = rect.top + rect.height / 2;
      left = rect.right + padding;
    } else {
      top = rect.top + rect.height / 2;
      left = rect.left - tooltipWidth - padding;
    }

    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
    if (placement === 'bottom' && top + 200 > window.innerHeight) {
      top = rect.top - padding - 200;
    }
    if (placement === 'top') {
      top = top - 200;
    }
    if (top < 16) top = rect.bottom + padding;

    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: `${tooltipWidth}px`,
      zIndex: 10002,
    });
  }, [currentStep]);

  const findTarget = useCallback(() => {
    if (status !== 'active' || !currentStep) return;

    const el = document.querySelector(currentStep.targetSelector) as HTMLElement | null;
    if (!el) {
      retryCountRef.current += 1;
      if (retryCountRef.current >= MAX_RETRIES) {
        nextStep();
        return;
      }
      retryTimerRef.current = setTimeout(findTarget, RETRY_INTERVAL);
      return;
    }

    const initialRect = el.getBoundingClientRect();
    const needsScroll = shouldScrollTargetIntoView(initialRect);
    el.scrollIntoView({ behavior: needsScroll ? 'smooth' : 'auto', block: 'center', inline: 'nearest' });

    const positionTarget = () => {
      rafRef.current = requestAnimationFrame(() => {
        const geometry = getSpotlightGeometry(el);
        setSpotlight(geometry);
        positionTooltip(geometry.targetRect);
        setVisible(true);
      });
    };

    if (needsScroll) {
      settleTimerRef.current = setTimeout(positionTarget, 180);
      return;
    }

    positionTarget();
  }, [status, currentStep, nextStep, positionTooltip]);

  // Find target whenever step changes
  useEffect(() => {
    if (status !== 'active' || !currentStep) return;

    const timer = setTimeout(findTarget, 200);

    return () => {
      clearTimeout(timer);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [status, currentStep, findTarget]);

  // Monitor target presence
  useEffect(() => {
    if (!visible || status !== 'active' || !currentStep) return;

    const interval = setInterval(() => {
      const el = document.querySelector(currentStep.targetSelector);
      if (!el) {
        setVisible(false);
        setSpotlight(null);
        retryCountRef.current = 0;
        clearInterval(interval);
        retryTimerRef.current = setTimeout(findTarget, RETRY_INTERVAL);
      }
    }, PRESENCE_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [visible, status, currentStep, findTarget]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!visible || status !== 'active' || !currentStep) return;

    const reposition = () => {
      const el = document.querySelector(currentStep.targetSelector);
      if (!el) return;
      const geometry = getSpotlightGeometry(el as HTMLElement);
      setSpotlight(geometry);
      positionTooltip(geometry.targetRect);
    };

    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [visible, status, currentStep, positionTooltip]);

  if (status !== 'active' || !currentStep || !spotlight || !visible) return null;

  return createPortal(
    <>
      <svg className="fixed inset-0 z-[10000] pointer-events-none w-full h-full" aria-hidden="true">
        <defs>
          <mask id="setup-tour-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={spotlight.mask.x}
              y={spotlight.mask.y}
              width={spotlight.mask.width}
              height={spotlight.mask.height}
              rx={spotlight.mask.rx}
              ry={spotlight.mask.ry}
              fill="black"
              style={{ transition: 'all 0.18s ease-out' }}
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.5)"
          mask="url(#setup-tour-spotlight-mask)"
        />
      </svg>
      <div
        className="fixed z-[10001] pointer-events-none ring-2 ring-primary/40"
        style={{
          top: spotlight.frame.top,
          left: spotlight.frame.left,
          width: spotlight.frame.width,
          height: spotlight.frame.height,
          borderRadius: spotlight.frame.borderRadius,
          transition: 'all 0.18s ease-out',
        }}
      />
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className={cn(
          'rounded-xl border border-border bg-card shadow-lg p-4 animate-in fade-in-0 zoom-in-95',
          'pointer-events-auto'
        )}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-semibold text-foreground">{t(currentStep.title)}</h3>
          <button
            onClick={skipSetupTour}
            className="shrink-0 p-0.5 rounded-md hover:bg-muted transition-colors"
            aria-label="Skip tour"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{t(currentStep.text)}</p>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">
            {currentStepIndex + 1} / {totalSteps}
          </span>
          <div className="flex items-center gap-1.5">
            {currentStepIndex > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={prevStep}>
                {t('back')}
              </Button>
            )}
            <Button size="sm" className="h-7 text-xs px-3" onClick={nextStep}>
              {currentStepIndex === totalSteps - 1 ? t('tour.nav_finish') : t('tour.nav_next')}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
